import { createRoot } from "react-dom/client";
import App from "@/components/App";
import { normalizeExperienceBody } from "@/modules/experiencePayloadUtils";
import { buildDefaultVideoUrl } from "@/modules/experienceMediaUrls";
import { startMcpNarrowTheme } from "@/modules/mcpNarrowTheme";
import { isMcpAppSurface } from "@/modules/mcpSurface";
import { McpChunk } from "@/components/mcp/mcpChunk";

/*
 * Warm-only mode: this player frame was opened by the publisher-side warm
 * pipeline (see modules/experiencePrefetchWarm.js#_warmViaPlayIframe) solely
 * to populate the HTTP cache with the experience JSON and video bytes from
 * an allowed Referer (play.pixmoto.com). Skip the React mount and analytics;
 * just fetch the API and start a muted <video preload="auto">.
 */
const WARM_TARGET_BUFFER_SEC = 3;

function isWarmOnly() {
  try {
    return /[?&]pix-warm-only=1\b/.test(location.search);
  } catch (_e) {
    return false;
  }
}

function getWarmDevice() {
  try {
    const match = location.search.match(/[?&]pix-device=([^&]+)/);
    if (match) {
      return decodeURIComponent(match[1]);
    }
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
    ) {
      return "mobile";
    }
  } catch (_e) {
    /* ignore */
  }
  return "desktop";
}

function startBoundedPreload(videoUrl: string) {
  const video = document.createElement("video");
  let stopped = false;
  const stop = function () {
    if (stopped) {
      return;
    }
    stopped = true;
    try {
      video.removeEventListener("progress", onProgress);
      video.pause();
      video.preload = "none";
      video.removeAttribute("src");
      video.load();
    } catch (_e) {
      /* ignore */
    }
  };
  const onProgress = function () {
    try {
      if (video.buffered.length > 0) {
        const end = video.buffered.end(video.buffered.length - 1);
        if (end >= WARM_TARGET_BUFFER_SEC) {
          stop();
        }
      }
    } catch (_e) {
      /* ignore */
    }
  };

  video.muted = true;
  video.preload = "auto";
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.style.cssText =
    "position:absolute;width:1px;height:1px;left:-9999px;top:-9999px;opacity:0;pointer-events:none";
  video.addEventListener("progress", onProgress);
  (document.body || document.documentElement).appendChild(video);
  video.src = videoUrl;
}

function warmOnly() {
  const metadata =
    (window.pixmoto && window.pixmoto.experienceMetadata) || null;
  if (!metadata || !metadata.experienceUrl) {
    return;
  }
  fetch(metadata.experienceUrl, { credentials: "omit" })
    .then(function (res) {
      if (!res.ok) {
        return null;
      }
      return res.json();
    })
    .then(function (json) {
      const body = normalizeExperienceBody(json);
      if (!body || !body.video) {
        return;
      }
      const videoUrl = buildDefaultVideoUrl(body, {
        protocol: location.protocol,
        mediaServer: metadata.mediaServer,
        device: getWarmDevice(),
      });
      if (!videoUrl) {
        return;
      }
      startBoundedPreload(videoUrl);
    })
    .catch(function () {
      /* warm is best-effort; swallow */
    });
}

if (isWarmOnly()) {
  warmOnly();
} else {
  startMcpNarrowTheme();
  if (isMcpAppSurface()) {
    McpChunk.preload();
  }
  const appRoot = document.getElementById("app");
  if (appRoot) {
    appRoot.setAttribute("data-pix-version", process.env.PIXMOTO_PLAY_VERSION);
  }
  createRoot(appRoot as HTMLElement).render(<App />);
}
